import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Chip } from '@thoughtspot/radiant-react/widgets/chip';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { SafeHTML } from '@thoughtspot/radiant-react/widgets/safe-html';
import { TableListView } from '@thoughtspot/radiant-react/widgets/table/table-list-view';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getCurrentOrgId, getDocumentationLink, } from '@thoughtspot/session-service';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BasePageContext } from "../../../../contexts/basepage-context";
import { useGetLLMGatewayTenantOrgMappings, } from "../../../../services/llm-gateway/llm-gateway-service";
import { LLM_PROVIDER, PROVIDER_CONFIG } from '../llm-gateway-constants';
import styles from './llm-visibility-table.module.scss';
const FALLBACK_COLUMNS = [
    { id: 'model', headerName: 'Model', minWidth: 200, sortable: true },
    { id: 'provider', headerName: 'Provider', minWidth: 160, sortable: true },
    { id: 'type', headerName: 'Type', minWidth: 180, sortable: true },
    { id: 'endpoint', headerName: 'Endpoint', minWidth: 120, sortable: true },
    { id: 'status', headerName: 'Status', minWidth: 120, sortable: true },
    { id: 'services', headerName: 'Services', minWidth: 260, sortable: true },
];
const getProviderIcon = (icon) => {
    const key = (icon?.toUpperCase() ?? '');
    return (PROVIDER_CONFIG[key]?.imageUrl ??
        PROVIDER_CONFIG[LLM_PROVIDER.CUSTOM].imageUrl);
};
const ModelCellRenderer = (params) => {
    const rowData = (params?.data ?? {});
    const modelLabel = params?.value ?? rowData?.model ?? '-';
    return (React.createElement("div", { className: styles.modelCell, title: modelLabel },
        React.createElement("img", { src: getProviderIcon(rowData?.icon), className: styles.modelIcon, alt: "", "aria-hidden": true }),
        React.createElement("span", { className: styles.ellipsisText }, modelLabel)));
};
const MAX_VISIBLE_SERVICES = 2;
const ServicesCellRenderer = (params) => {
    const services = params?.value ?? params?.data?.services ?? [];
    const moreRef = useRef(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    if (!services.length)
        return React.createElement("span", null, "-");
    const visible = services.slice(0, MAX_VISIBLE_SERVICES);
    const remaining = services.slice(MAX_VISIBLE_SERVICES);
    return (React.createElement("div", { className: styles.servicesCell },
        visible.map((service) => (React.createElement(Chip.Trigger, { key: service, nonInteractive: true, content: [{ text: service, style: 'regular' }], className: styles.serviceChip }))),
        remaining.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("span", { ref: moreRef, className: styles.servicesMoreBadge, onClick: () => setIsPopoverOpen(prev => !prev), onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsPopoverOpen(prev => !prev);
                    }
                }, role: "button", tabIndex: 0 }, `+${remaining.length}`),
            React.createElement(Popover, { align: "bottom-start", targetRef: moreRef, isVisible: isPopoverOpen, onClickOutside: () => setIsPopoverOpen(false), content: React.createElement(Vertical, { className: styles.servicesPopoverContent }, remaining.map((service) => (React.createElement(Typography, { key: service, variant: "body-normal", noMargin: true, className: styles.servicesPopoverItem }, service)))) })))));
};
const DefaultCellRenderer = (params) => {
    const value = params?.value;
    const displayValue = value ?? '-';
    return (React.createElement("span", { className: styles.ellipsisText, title: displayValue }, displayValue));
};
const CELL_RENDERERS = {
    model: ModelCellRenderer,
    services: ServicesCellRenderer,
};
const buildTableColumns = (apiColumns) => {
    const columns = apiColumns?.length ? apiColumns : FALLBACK_COLUMNS;
    return columns.map(col => {
        const colDef = {
            colId: col.id,
            field: col.id,
            headerName: col.headerName,
            minWidth: col.minWidth ?? 140,
            width: col.width,
            maxWidth: col.maxWidth,
            sortable: col.sortable ?? true,
        };
        if (CELL_RENDERERS[col.id]) {
            colDef.cellRenderer = CELL_RENDERERS[col.id];
        }
        else {
            colDef.cellRenderer = DefaultCellRenderer;
            colDef.cellClass = styles.ellipsisCell;
        }
        return colDef;
    });
};
export const LlmVisibilityTable = ({ onBack, sageFeatureConfigs, }) => {
    const { t } = useTranslation();
    const { sessionService, metricsService } = BasePageContext();
    const tenantId = sessionService?.getClusterId?.() ?? '';
    const orgId = String(getCurrentOrgId() ?? '');
    const { data, loading, error, responseError, } = useGetLLMGatewayTenantOrgMappings(tenantId, orgId, sageFeatureConfigs);
    const tableData = useMemo(() => {
        if (!data?.mappings)
            return [];
        return data.mappings.map((row, idx) => ({
            id: String(row.assignedConnectionId ?? idx),
            ...row,
        }));
    }, [data]);
    const tableColumns = buildTableColumns(data?.columns);
    const hasError = Boolean(error || responseError);
    const errorMessage = responseError?.errorMessage ?? 'Failed to load LLM configurations.';
    const subtitle = t('aiModelType.subTitle', {
        href: 'https://support.thoughtspot.com',
    });
    useEffect(() => {
        metricsService.collectEvent(new UserAction(userActionTypeEnum.LLM_CONNECTIONS_PAGE_VIEWED), {
            tenantId,
            orgId,
        });
    }, []);
    useEffect(() => {
        if (hasError) {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.LLM_CONNECTIONS_ERROR_SHOWN), {
                errorType: errorMessage,
                tenantId,
                orgId,
            });
        }
    }, [hasError, errorMessage]);
    return (React.createElement(Vertical, { className: styles.container },
        React.createElement(Horizontal, { className: styles.backRow },
            React.createElement(Button, { icon: IconID.ARROW_LEFT, type: "secondary", backgroundType: "light", size: "s", onClick: onBack, ariaLabel: t('Back'), className: styles.backButton }),
            React.createElement(Typography, { variant: "body-normal" }, t('tscli.config.sage_feature.title'))),
        React.createElement(Vertical, { className: styles.headerBlock },
            React.createElement(Typography, { variant: "h2" }, t('aiModelType.title')),
            React.createElement(Horizontal, { className: styles.subtitleRow },
                React.createElement(Typography, { variant: "body-normal", color: Colors.gray, className: styles.subtitle },
                    React.createElement(SafeHTML, { html: subtitle, config: { ADD_ATTR: ['target'] } })),
                React.createElement(Link, { href: getDocumentationLink('/spotter-byollmk'), target: "_blank", rel: "noreferrer" }, t('tscli.ui.learnmore.label')))),
        hasError ? (React.createElement(Alert.Banner, { isVisible: true, type: BannerAlertType.ERROR, message: errorMessage, showCloseButton: false, className: styles.errorBanner })) : (React.createElement("div", { className: styles.tableWrapper },
            React.createElement(TableListView, { key: data?.columns ? 'api' : 'fallback', data: tableData, columns: tableColumns, options: { hasCheckboxes: false }, loading: loading })))));
};
//# sourceMappingURL=llm-visibility-table.container.js.map